import { ReactNode } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  colorLandingBrand,
  colorLandingFooterBorder,
  colorLandingFooterLink,
  colorLandingFooterText,
  colorLandingInk,
  colorLandingSurface,
  fontLandingDisplay,
} from "src/design/tokens";
import { supportUrl } from "src/helpers/urls";
import { landingLinks } from "./landingLinks";
import { useSectionNavigation } from "./useSectionNavigation";
import { AppleIcon, InstagramIcon, PlayIcon } from "./components/StoreIcons";

interface FooterLinkProps {
  label: string;
  /** In-app route. */
  to?: string;
  /** External destination, opened in a new tab. */
  href?: string;
  /** Landing section to scroll to; stays a real link so it can still be opened in a new tab. */
  sectionId?: string;
}

const linkSx = {
  display: "block",
  fontSize: 14,
  mb: 1.25,
  color: colorLandingFooterText,
  textDecoration: "none",
  cursor: "pointer",
  "&:hover": { color: colorLandingSurface },
};

const FooterLink = ({ label, to, href, sectionId }: FooterLinkProps) => {
  const scrollToSection = useSectionNavigation();

  if (sectionId) {
    return (
      <Typography
        component="a"
        href={`/#${sectionId}`}
        onClick={(event: React.MouseEvent) => {
          // Let modified clicks (new tab/window) behave like any other link.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
            return;
          }
          event.preventDefault();
          scrollToSection(sectionId);
        }}
        sx={linkSx}
      >
        {label}
      </Typography>
    );
  }

  return (
    <Typography
      component={to ? Link : "a"}
      to={to}
      href={href}
      target={href ? "_blank" : undefined}
      rel={href ? "noopener noreferrer" : undefined}
      sx={linkSx}
    >
      {label}
    </Typography>
  );
};

interface StoreChipProps {
  label: string;
  href: string;
  icon: ReactNode;
}

const StoreChip = ({ label, href, icon }: StoreChipProps) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      border: `1px solid ${colorLandingFooterBorder}`,
      borderRadius: "10px",
      px: 1.75,
      py: 1,
      fontSize: 13,
      color: colorLandingFooterLink,
      textDecoration: "none",
      "&:hover": { borderColor: colorLandingBrand, color: colorLandingSurface },
    }}
  >
    {icon}
    {label}
  </Box>
);

interface FooterColumnProps {
  title: string;
  children: ReactNode;
}

const FooterColumn = ({ title, children }: FooterColumnProps) => (
  <Grid item xs={6} md={2}>
    <Typography
      component="h5"
      sx={{
        color: colorLandingSurface,
        fontSize: 14,
        fontWeight: 600,
        mb: 1.75,
      }}
    >
      {title}
    </Typography>
    {children}
  </Grid>
);

/**
 * Landing footer, also reused by the standalone public pages: the wordmark and
 * tagline, the store links, then product / company / legal columns.
 */
export const LandingFooter = () => {
  const { t } = useTranslation();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: colorLandingInk,
        color: colorLandingFooterText,
        pt: { xs: 6, md: 8 },
        pb: 4.25,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4.25}>
          <Grid item xs={12} md={6}>
            <Typography
              component={Link}
              to="/"
              sx={{
                display: "block",
                fontFamily: fontLandingDisplay,
                fontSize: 24,
                color: colorLandingSurface,
                textDecoration: "none",
                mb: 1.75,
              }}
            >
              L'addition
            </Typography>
            <Typography sx={{ fontSize: 14, maxWidth: "34ch", lineHeight: 1.6 }}>
              {t("landingPage.footer.tagline")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.25, mt: 2.25, flexWrap: "wrap" }}>
              <StoreChip
                label={t("landingPage.footer.googlePlay")}
                href={landingLinks.GOOGLE_PLAY}
                icon={<PlayIcon size={15} />}
              />
              <StoreChip
                label={t("landingPage.footer.appStore")}
                href={landingLinks.APP_STORE}
                icon={<AppleIcon size={16} />}
              />
              <StoreChip
                label="Instagram"
                href={landingLinks.INSTAGRAM}
                icon={<InstagramIcon size={15} />}
              />
            </Box>
          </Grid>

          <FooterColumn title={t("landingPage.footer.product")}>
            <FooterLink
              label={t("landingPage.footer.features")}
              sectionId="fonctionnalites"
            />
            <FooterLink
              label={t("landingPage.footer.analytics")}
              sectionId="analytiques"
            />
            <FooterLink label={t("landingPage.footer.pricing")} sectionId="tarifs" />
          </FooterColumn>

          <FooterColumn title={t("landingPage.footer.company")}>
            <FooterLink label={t("landingPage.footer.requestDemo")} sectionId="demo" />
            <FooterLink label={t("landingPage.footer.contact")} href={supportUrl} />
            <FooterLink
              label={t("landingPage.footer.instagram")}
              href={landingLinks.INSTAGRAM}
            />
          </FooterColumn>

          <FooterColumn title={t("landingPage.footer.legal")}>
            <FooterLink label={t("landingPage.footer.terms")} to="/terms" />
            <FooterLink
              label={t("landingPage.footer.privacy")}
              to="/MobileAccountDeletion"
            />
          </FooterColumn>
        </Grid>

        <Box
          sx={{
            mt: 6,
            pt: 3,
            borderTop: `1px solid ${colorLandingFooterBorder}`,
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            fontSize: 13,
          }}
        >
          <Typography sx={{ fontSize: 13 }}>
            {t("landingPage.footer.copyright")}
          </Typography>
          <Typography sx={{ fontSize: 13 }}>
            {t("landingPage.footer.madeIn")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
